import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NgdrawerComponent } from './modules/landing/experimental/ngdrawer/ngdrawer.component';
import { FormlyComponent } from './modules/landing/experimental/formly/formly.component';
import { TabformviewComponent } from './modules/landing/experimental/tabformview/tabformview.component';
import { SfDiagramComponent } from './modules/landing/experimental/sf-diagram/sf-diagram.component';

// prettier-ignore
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/dashboards/project'
    { path: '', pathMatch: 'full', redirectTo: 'sdf-home' },

    // Redirect signed-in user to the '/dashboards/project'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    {
        path: 'signed-in-redirect',
        pathMatch: 'full',
        redirectTo: 'sdf-home',
    },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () =>
                    import(
                        'app/modules/auth/confirmation-required/confirmation-required.routes'
                    ),
            },
            {
                path: 'sign-in',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.routes'),
            },
        ],
    },
    {
        path: 'docs',
        children: [
            // Changelog
            {
                path: 'changelog',
                loadChildren: () =>
                    import(
                        'app/modules/public/docs/changelog/changelog.routes'
                    ),
            },

            // Guides
            {
                path: 'guides',
                loadChildren: () =>
                    import('app/modules/public/docs/guides/guides.routes'),
            },
        ],
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            {
                path: 'sdf-home',
                loadChildren: () =>
                    import('app/modules/sdf/home/sdf-home/home.routes'),
            },
            {
                path: 'edm-dashboard',
                loadChildren: () => import('app/modules/sdf/edm/edm.routes'),
            },
            {
                path: 'ekc',
                loadChildren: () =>
                    import('app/modules/sdf/edm/ekc/ekc.routes'),
            },
            {
                path: 'edm',
                children: [
                    {
                        path: 'cdp',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/cdp/cdp.routes'
                            ),
                    },
                    {
                        path: 'docs',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/docs/docs.routes'
                            ),
                    },
                ],
            },
        ],
    },
    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () =>
                    import('app/modules/auth/sign-out/sign-out.routes'),
            },
        ],
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'home',
                loadChildren: () =>
                    import('app/modules/landing/home/home.routes'),
            },
            {
                path: 'help-center',
                loadChildren: () =>
                    import('app/modules/public/help-center/help-center.routes'),
            },
            {
                path: 'experimental/ngdrawer',
                component: NgdrawerComponent,
            },
            {
                path: 'experimental/formly',
                component: FormlyComponent,
            },
            {
                path: 'experimental/tabformview',
                component: TabformviewComponent,
            },
            {
                path: 'experimental/diagram1',
                component: SfDiagramComponent,
            },
        ],
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver,
        },
        children: [
            // Dashboards
            {
                path: 'dashboards',
                children: [
                    {
                        path: 'edm',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/dashboards/edm/edm-dashboard.routes'
                            ),
                    },
                    {
                        path: 'project',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/project/project.routes'
                            ),
                    },
                    {
                        path: 'analytics',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/analytics/analytics.routes'
                            ),
                    },
                    {
                        path: 'finance',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/finance/finance.routes'
                            ),
                    },
                    {
                        path: 'crypto',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/dashboards/crypto/crypto.routes'
                            ),
                    },
                ],
            },

            // Apps
            {
                path: 'ekc',
                children: [
                    {
                        path: 'tms',
                        loadChildren: () =>
                            import('app/modules/admin/apps/tms/tms.routes'),
                    },
                    {
                        path: 'dataelements',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/dataelements/data-element.routes'
                            ),
                    },
                    {
                        path: 'dataentities',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/dataentities/data-entity.routes'
                            ),
                    },
                    {
                        path: 'dataassets/ontologydomains',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/ontologydomains/ontology-domain.routes'
                            ),
                    },
                    {
                        path: 'dataassets/datashapes',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/datashapes/data-shape.routes'
                            ),
                    },
                    {
                        path: 'dataassets/relationshipdiagrams',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/relationshipdiagrams/relationship-diagrams.routes'
                            ),
                    },
                    {
                        path: 'dataassets/externalontology',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/external-ontology-import/external-ontology-import.routes'
                            ),
                    },
                    {
                        path: 'dataassets/vkg',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/vkg/vkg.routes'
                            ),
                    },
                    {
                        path: 'dataassets/businessterms',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/data-assets/business-terms/business-terms.routes'
                            ),
                    },
                    {
                        path: 'entitysearch',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/entitysearch/entitysearch.routes'
                            ),
                    },
                    {
                        path: 'datasets',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/datasets/datasets.routes'
                            ),
                    },
                    {
                        path: 'mailtemplates',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/mailtemplates/mail-templates.routes'
                            ),
                    },
                    {
                        path: 'rulecatalog',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/ekc/rulecatalog/rule-catalog.routes'
                            ),
                    },
                    //{path: 'accounts', loadChildren: () => import('app/modules/admin/apps/contacts/contacts.routes')},
                    //{path: 'academy', loadChildren: () => import('app/modules/admin/apps/academy/academy.routes')},
                    //{path: 'chat', loadChildren: () => import('app/modules/admin/apps/chat/chat.routes')},
                    {
                        path: 'contacts',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/apps/contacts/contacts.routes'
                            ),
                    },
                    //{path: 'ecommerce', loadChildren: () => import('app/modules/admin/apps/ecommerce/ecommerce.routes')},
                    //{path: 'file-manager', loadChildren: () => import('app/modules/admin/apps/file-manager/file-manager.routes')},
                    // {
                    //     path: 'help-center',
                    //     loadChildren: () => import('app/modules/admin/apps/help-center/help-center.routes'),
                    // },
                    //{path: 'mailbox', loadChildren: () => import('app/modules/admin/apps/mailbox/mailbox.routes')},
                    //{path: 'notes', loadChildren: () => import('app/modules/admin/apps/notes/notes.routes')},
                    //{path: 'scrumboard', loadChildren: () => import('app/modules/admin/apps/scrumboard/scrumboard.routes')},
                    //{path: 'tasks', loadChildren: () => import('app/modules/admin/apps/tasks/tasks.routes')},
                ],
            },
            {
                path: 'data-mgmt',
                children: [
                    {
                        path: 'datasets',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/datasets/datasets.routes'
                            ),
                    },
                    {
                        path: 'glossary',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/glossary/glossary.routes'
                            ),
                    },
                    {
                        path: 'refdata',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/reference-data-management/reference-data-management.routes'
                            ),
                    },
                    //dqm app
                    {
                        path: 'dqm',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/dqm/dqm.routes'
                            ),
                    },
                    //data marketplace app
                    {
                        path: 'marketplace',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/marketplace/marketplace.routes'
                            ),
                    },
                    {
                        path: 'vkg-browser',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/vkg-browser/vkg-browser.routes'
                            ),
                    },
                    {
                        path: 'es-connectors',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/es-connectors/es-connectors.routes'
                            ),
                    },
                    {
                        path: 'review-queue',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/review-queue/review-queue.routes'
                            ),
                    },
                    {
                        path: 'batch-update-review',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/batch-update-review/batch-update-review.routes'
                            ),
                    },
                    // SDF2026-495: steward-override reviews, beside its Data Governance siblings.
                    {
                        path: 'override-reviews',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/override-review/override-review.routes'
                            ),
                    },
                    {
                        path: 'create-entity',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/entity-crud/entity-crud.routes'
                            ),
                    },
                    {
                        path: 'edit-entity',
                        loadChildren: () =>
                            import(
                                'app/modules/secure/apps/edm/data-management/entity-crud/edit-entity.routes'
                            ),
                    }
                ],
            },
            {
                path: 'linkedmdm',
                loadChildren: () =>
                    import('app/modules/secure/apps/lmdm/lmdm.routes'),
            },
            {
                path: 'events',
                loadChildren: () =>
                    import('app/modules/secure/apps/events/events.routes'),
            },
            //Data Unification
            {
                path: 'du',
                loadChildren: () =>
                    import('app/modules/secure/apps/edm/ekc/du/du.routes'),
            },
            //Linked Apps catalog (SDA/External applications)
            {
                path: 'linked-apps',
                loadChildren: () =>
                    import('app/modules/secure/apps/linked-apps/linked-apps.routes'),
            },
            //Admin
            {
                path: 'admin',
                loadChildren: () =>
                    import('app/modules/secure/apps/edm/cdm/admin/admin.routes'),
            },
            //ero app
            {
                path: 'ero',
                loadChildren: () =>
                    import('app/modules/secure/apps/ero/ero.routes'),
            },
            //OntoAsk
            {
                path: 'ontoask',
                loadChildren: () =>
                    import('app/modules/secure/apps/ontoask/ontoask.routes'),
            },
            //Data Governance
            {
                path: 'data-governance',
                loadChildren: () =>
                    import('app/modules/secure/apps/data-governance/data-governance.routes'),
            },

            // Pages
            {
                path: 'pages',
                children: [
                    // Activities
                    //{path: 'activities', loadChildren: () => import('app/modules/admin/pages/activities/activities.routes')},

                    // Authentication
                    //{path: 'authentication', loadChildren: () => import('app/modules/admin/pages/authentication/authentication.routes')},

                    // Coming Soon
                    {
                        path: 'coming-soon',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/coming-soon/coming-soon.routes'
                            ),
                    },

                    // Error
                    {
                        path: 'error',
                        children: [
                            {
                                path: '404',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/error/error-404/error-404.routes'
                                    ),
                            },
                            {
                                path: '500',
                                loadChildren: () =>
                                    import(
                                        'app/modules/admin/pages/error/error-500/error-500.routes'
                                    ),
                            },
                        ],
                    },

                    // Invoice
                    // {path: 'invoice', children: [
                    //     {path: 'printable', children: [
                    //         {path: 'compact', loadChildren: () => import('app/modules/admin/pages/invoice/printable/compact/compact.routes')},
                    //         {path: 'modern', loadChildren: () => import('app/modules/admin/pages/invoice/printable/modern/modern.routes')}
                    //     ]}
                    // ]},

                    // Maintenance
                    {
                        path: 'maintenance',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/maintenance/maintenance.routes'
                            ),
                    },

                    // Profile
                    {
                        path: 'profile',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/profile/profile.routes'
                            ),
                    },

                    // Settings
                    {
                        path: 'settings',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/pages/settings/settings.routes'
                            ),
                    },
                ],
            },

            // User Interface
            {
                path: 'ui',
                children: [
                    // Material Components
                    //{path: 'material-components', loadChildren: () => import('app/modules/admin/ui/material-components/material-components.routes')},
                    // Fuse Components
                    //{path: 'fuse-components', loadChildren: () => import('app/modules/admin/ui/fuse-components/fuse-components.routes')},
                    // Other Components
                    //{path: 'other-components', loadChildren: () => import('app/modules/admin/ui/other-components/other-components.routes')},
                    // TailwindCSS
                    // {path: 'tailwindcss', loadChildren: () => import('app/modules/admin/ui/tailwindcss/tailwindcss.routes')},
                    // Advanced Search
                    //{path: 'advanced-search', loadChildren: () => import('app/modules/admin/ui/advanced-search/advanced-search.routes')},
                    // Animations
                    //{path: 'animations', loadChildren: () => import('app/modules/admin/ui/animations/animations.routes')},
                    // Cards
                    //{path: 'cards', loadChildren: () => import('app/modules/admin/ui/cards/cards.routes')},
                    // Colors
                    //{path: 'colors', loadChildren: () => import('app/modules/admin/ui/colors/colors.routes')},
                    // Confirmation Dialog
                    //{path: 'confirmation-dialog', loadChildren: () => import('app/modules/admin/ui/confirmation-dialog/confirmation-dialog.routes')},
                    // Datatable
                    //{path: 'datatable', loadChildren: () => import('app/modules/admin/ui/datatable/datatable.routes')},
                    // Forms
                    //{path: 'forms', loadChildren: () => import('app/modules/admin/ui/forms/forms.routes')},
                    // Icons
                    //{path: 'icons', loadChildren: () => import('app/modules/admin/ui/icons/icons.routes')},
                    // Page Layouts
                    //{path: 'page-layouts', loadChildren: () => import('app/modules/admin/ui/page-layouts/page-layouts.routes')},
                    // Typography
                    //{path: 'typography', loadChildren: () => import('app/modules/admin/ui/typography/typography.routes')}
                ],
            },

            // Documentation

            // 404 & Catch all
            // NO pathMatch: 'full' on either of these. With loadChildren it never matches: the
            // parent consumes the whole path and leaves nothing for the child's path:'' to match,
            // so /404-not-found failed to resolve and the catch-all below redirected every unknown
            // URL to a route that could not resolve either. Both children are path:'' so plain
            // prefix matching is what they need.
            {
                path: '404-not-found',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-404/error-404.routes'
                    ),
            },
            {
                path: 'error-500',
                loadChildren: () =>
                    import(
                        'app/modules/admin/pages/error/error-500/error-500.routes'
                    ),
            },
            { path: '**', redirectTo: '404-not-found' },
        ],
    },
];
